
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Shield, Zap, Target, ClipboardList, Package, Plus, Filter, Search, Edit3, Trash2 } from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import { AnimatePresence } from 'framer-motion';
import DiagnosticForm from '../components/forms/DiagnosticForm';
import {
  annoncerErreur, annoncerSucces, confirmer, EtatErreur, EtatSansDonnees, Squelette, SqueletteTableau,
} from '../etats';

const INTELLIGENCE_TABS = [
  { id: 'FORCE', label: 'Forces', icon: Shield, color: 'text-[var(--accent-texte)]' },
  { id: 'FAIBLESSE', label: 'Faiblesses', icon: Zap, color: 'text-[var(--attention)]' },
  { id: 'ACTIONS', label: 'Actions correctives', icon: ClipboardList, color: 'text-[var(--text-bold)]' },
];

const CONFIG_TABS = [
  { id: 'niveaux', label: 'Niveaux de maturité', icon: Target },
  { id: 'livrables', label: 'Livrables', icon: Package },
];

export default function Diagnostics({ user }) {
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const chargeRef = useRef(false);
  const [erreur, setErreur] = useState(null);
  const [mainTab, setMainTab] = useState('intelligence');
  const [intelligenceTab, setIntelligenceTab] = useState('FORCE');
  const [configTab, setConfigTab] = useState('niveaux');

  const [data, setData] = useState({
    intelligence: [],
    actions: [],
    niveaux: [],
    livrables: [],
    structure: [],
  });

  const [filterAxe, setFilterAxe] = useState('all');
  const [filterNiveau, setFilterNiveau] = useState('all');
  const [filterLivrableNiveau, setFilterLivrableNiveau] = useState('all');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: null, type: 'FORCE', description: '', niveau_id: '', sous_domaine_id: '',
    action_id: '', libelle: '', min_score: '', max_score: '',
  });

  const isReadOnly = user?.role === 'ANALYSTE';

  useEffect(() => { loadAllData(); }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [intel, acts, nivs, livr, struct] = await Promise.all([
        adminApi.getIntelligence(),
        adminApi.getActionsBase(),
        adminApi.getNiveaux(),
        adminApi.getLivrables(),
        adminApi.getStructure(),
      ]);
      setData({
        intelligence: intel ?? [],
        actions: acts ?? [],
        niveaux: nivs ?? [],
        livrables: livr ?? [],
        structure: struct ?? [],
      });
      setErreur(null);
      chargeRef.current = true;
      setCharge(true);
    } catch (e) {
      console.error(e);
      // Une actualisation qui échoue garde la matrice affichée.
      if (chargeRef.current) annoncerErreur(e?.message || 'La matrice n’a pas pu être actualisée.');
      else setErreur(e);
    } finally { setLoading(false); }
  };

  const flatSubdomains = useMemo(() => {
    return (data.structure || []).flatMap(dom => (dom.subdomains || []).map(sd => ({ ...sd, domaine_nom: dom.nom })));
  }, [data.structure]);

  const allSubdomains = useMemo(() => {
    const unique = new Map();
    flatSubdomains.forEach(sd => {
      if (!unique.has(sd.id)) unique.set(sd.id, { id: sd.id, nom: sd.nom });
    });
    return Array.from(unique.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [flatSubdomains]);

  const filteredIntelligence = useMemo(() => {
    let list = mainTab === 'intelligence' && intelligenceTab === 'ACTIONS'
      ? data.actions
      : data.intelligence.filter(i => i.type === intelligenceTab);
    if (filterAxe !== 'all') {
      list = list.filter(i => String(i.sous_domaine_id || '') === String(filterAxe));
    }
    if (filterNiveau !== 'all') {
      list = list.filter(i => String(i.niveau_id || '') === String(filterNiveau));
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(i =>
        (i.description || '').toLowerCase().includes(s) ||
        (i.sous_domaine_nom || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [data.intelligence, data.actions, mainTab, intelligenceTab, filterAxe, filterNiveau, search]);

  const filteredLivrables = useMemo(() => {
    let list = data.livrables || [];
    if (filterLivrableNiveau !== 'all') {
      list = list.filter(i => String(i.niveau_id || '') === String(filterLivrableNiveau));
    }
    return list;
  }, [data.livrables, filterLivrableNiveau]);

  const handleOpenModal = (item = null) => {
    if (isReadOnly) return;
    if (mainTab === 'config' && configTab === 'niveaux') {
      setFormData(item ? { ...formData, id: item.id, libelle: item.libelle, min_score: item.min_score, max_score: item.max_score } : { ...formData, id: null, libelle: '', min_score: '', max_score: '' });
    } else if (mainTab === 'config' && configTab === 'livrables') {
      setFormData(item ? { ...formData, id: item.id, description: item.description, sous_domaine_id: item.sous_domaine_id, niveau_id: item.niveau_id, action_id: item.action_id || '' } : { ...formData, id: null, description: '', action_id: '', sous_domaine_id: flatSubdomains[0]?.id ?? '', niveau_id: data.niveaux[0]?.id ?? '' });
    } else if (intelligenceTab === 'ACTIONS') {
      setFormData(item ? { ...formData, id: item.id, description: item.description, sous_domaine_id: item.sous_domaine_id, niveau_id: item.niveau_id } : { ...formData, id: null, description: '', sous_domaine_id: '', niveau_id: '' });
    } else {
      setFormData(item ? { ...formData, ...item } : { ...formData, id: null, type: intelligenceTab, description: '', niveau_id: '', sous_domaine_id: '' });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mainTab === 'config' && configTab === 'niveaux') {
        await adminApi.saveNiveau({ id: formData.id, libelle: formData.libelle, min_score: Number(formData.min_score), max_score: Number(formData.max_score) });
      } else if (mainTab === 'config' && configTab === 'livrables') {
        await adminApi.saveLivrable({ id: formData.id, description: formData.description, sous_domaine_id: formData.sous_domaine_id, niveau_id: formData.niveau_id, action_id: formData.action_id || null });
      } else if (intelligenceTab === 'ACTIONS') {
        await adminApi.saveActionBase({ id: formData.id, description: formData.description, sous_domaine_id: formData.sous_domaine_id, niveau_id: formData.niveau_id });
      } else {
        await adminApi.saveIntelligence({ id: formData.id, type: formData.type, description: formData.description, niveau_id: formData.niveau_id, sous_domaine_id: formData.sous_domaine_id });
      }
      setShowModal(false);
      annoncerSucces(formData.id ? 'Modification enregistrée.' : 'Élément ajouté.');
      loadAllData();
    } catch (err) { annoncerErreur(err?.message || 'Erreur de sauvegarde.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (isReadOnly || !await confirmer('Supprimer définitivement cet élément ?')) return;
    try {
      if (mainTab === 'config' && configTab === 'niveaux') await adminApi.deleteNiveau(id);
      else if (mainTab === 'config' && configTab === 'livrables') await adminApi.deleteLivrable(id);
      else if (intelligenceTab === 'ACTIONS') await adminApi.deleteActionBase(id);
      else await adminApi.deleteIntelligence(id);
      annoncerSucces('Élément supprimé.');
      loadAllData();
    } catch (err) { annoncerErreur(err?.message || 'Action impossible.'); }
  };

  // Premier chargement seulement : après une action, la table reste affichée.
  if (loading && !charge) {
    return (
      <div className="space-y-8">
        <div>
          <Squelette className="h-8 w-56 mb-3" />
          <Squelette className="h-3 w-96 max-w-full" />
        </div>
        <Squelette className="h-11 w-96 max-w-full" />
        <SqueletteTableau colonnes={6} />
      </div>
    );
  }

  if (erreur && !charge) {
    return (
      <div className="space-y-8">
        <h1 className="titre-page">Diagnostics</h1>
        <div className="carte-tableau"><EtatErreur erreur={erreur} onReessayer={loadAllData} /></div>
      </div>
    );
  }

  // Le vide dit pourquoi : une recherche, un filtre, ou rien encore.
  const ajouter = !isReadOnly && (
    <button type="button" onClick={() => handleOpenModal()} className="act act-sm">
      <Plus className="w-4 h-4" /> Ajouter
    </button>
  );
  const videIntelligence = search.trim() ? (
    <EtatSansDonnees cas="sansResultat" compact titre={`Aucun résultat pour « ${search.trim()} »`}
      action={<button type="button" className="act act-sm act-contour" onClick={() => setSearch('')}>Effacer la recherche</button>} />
  ) : filterAxe !== 'all' || filterNiveau !== 'all' ? (
    <EtatSansDonnees cas="sansFiltre" compact
      action={<button type="button" className="act act-sm act-contour" onClick={() => { setFilterAxe('all'); setFilterNiveau('all'); }}>Réinitialiser</button>} />
  ) : (
    <EtatSansDonnees cas="vide" compact titre="Aucune donnée" aide="Rien n’est encore saisi dans cette vue." action={ajouter} />
  );

  return (
    <div className="space-y-8 animate-in">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
        <div>
          <h1 className="titre-page">Diagnostics</h1>
          <p className="text-[var(--text-muted)] font-medium mt-1">Intelligence experte : Constats, Actions, Niveaux & Livrables. Pour une révision d'ensemble, passez par le classeur de la page Référentiel.</p>
        </div>
      </div>

      {/* Onglets principaux.
          « Niveaux & Livrables » testait `configTab === 'niveaux'` au lieu de
          `mainTab === 'config'`. Comme `configTab` vaut 'niveaux' par defaut,
          ce bouton restait allume en permanence : les deux onglets semblaient
          actifs en meme temps.
          L'encre sur l'accent passe de `--bg-main` (1,9:1 sur le vert, donc
          illisible) au bleu nuit deja employe par le menu principal. */}
      <div role="tablist" aria-label="Sections du referentiel expert"
           className="segmente max-w-full overflow-x-auto">
        {[
          { cle: 'intelligence', libelle: 'Intelligence & Actions', Icone: Shield },
          { cle: 'config', libelle: 'Niveaux & Livrables', Icone: Target },
        ].map(({ cle, libelle, Icone }) => (
          <button
            key={cle}
            type="button"
            role="tab"
            aria-selected={mainTab === cle}
            onClick={() => setMainTab(cle)}
            className="whitespace-nowrap shrink-0"
          >
            <Icone className="w-4 h-4" aria-hidden="true" /> {libelle}
          </button>
        ))}
      </div>

      {mainTab === 'intelligence' && (
        <>
          {/* Trois vues d'une même matière, exclusives : le contrôle
              segmenté du modèle. L'icône suit la couleur du texte plutôt
              que de garder la sienne — sur la pilule encre, un bleu
              d'origine ne se voyait plus. */}
          <div className="segmente max-w-full overflow-x-auto" role="tablist"
               aria-label="Vue du référentiel expert">
            {INTELLIGENCE_TABS.map(t => (
              <button key={t.id} type="button" role="tab"
                      aria-selected={intelligenceTab === t.id}
                      onClick={() => setIntelligenceTab(t.id)}
                      className="whitespace-nowrap shrink-0">
                <t.icon className="w-4 h-4" strokeWidth={1.8} /> {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-[var(--bg-card)] shadow-carte rounded-carte">
            <div className="space-y-2">
              <label className="text-[9px] font-semibold uppercase text-[var(--text-muted)] tracking-widest pl-2">Sous-domaine</label>
              <div className="relative">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <select className="filtre filtre-icone" value={filterAxe} onChange={e => setFilterAxe(e.target.value)}>
                  <option value="all">Tous les sous-domaines</option>
                  {allSubdomains.map(sd => <option key={sd.id} value={sd.id}>{sd.nom}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-semibold uppercase text-[var(--text-muted)] tracking-widest pl-2">Niveau</label>
              <div className="relative">
                <Target className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <select className="filtre filtre-icone" value={filterNiveau} onChange={e => setFilterNiveau(e.target.value)}>
                  <option value="all">Tous les niveaux</option>
                  {data.niveaux.map(n => <option key={n.id} value={n.id}>{n.libelle}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-semibold uppercase text-[var(--text-muted)] tracking-widest pl-2">Recherche</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <input type="text" placeholder="Mots-clés..." className="champ champ-loupe font-bold" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
          {!isReadOnly && (
            <div className="flex justify-end">
              <button type="button" onClick={() => handleOpenModal()} className="act">
                <Plus className="w-4 h-4" /> Ajouter {intelligenceTab === 'ACTIONS' ? 'une action' : intelligenceTab === 'FORCE' ? 'une force' : 'une faiblesse'}
              </button>
            </div>
          )}
          <div className="carte-tableau">
            <div className="tableau-hote">
              <table className="w-full text-left min-w-[720px]">
                <thead className="">
                  <tr>
                    <th className="w-16">N°</th>
                    {(intelligenceTab === 'FORCE' || intelligenceTab === 'FAIBLESSE') && (
                      <>
                        <th className="">Type</th>
                        <th className="">Sous-domaine</th>
                        <th className="">Niveau</th>
                        <th className="">Description</th>
                      </>
                    )}
                    {intelligenceTab === 'ACTIONS' && (
                      <>
                        <th className="">Sous-domaine / Niveau</th>
                        <th className="">Action corrective</th>
                        <th className="">Livrables</th>
                      </>
                    )}
                    <th className="text-right w-28">Pilotage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {filteredIntelligence.length === 0 && (<tr><td colSpan={8}>{videIntelligence}</td></tr>)}
                  {filteredIntelligence.map((item, index) => (
                    <tr key={item.id} className="hover:bg-[var(--bg-main)] group">
                      <td className="px-6 py-4 text-sm font-bold text-[var(--text-muted)]">{index + 1}</td>
                      {intelligenceTab !== 'ACTIONS' ? (
                        <>
                          <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded-pastille text-[9px] font-semibold uppercase ${item.type === 'FORCE' ? 'bg-[var(--accent)]/10 text-[var(--accent-texte)]' : 'bg-[var(--attention)]/10 text-[var(--attention)]'}`}>{item.type}</span></td>
                          <td className="px-6 py-4 text-sm font-medium text-[var(--text-bold)]">{item.sous_domaine_nom}</td>
                          <td className="px-6 py-4 text-xs text-[var(--text-muted)]">{item.niveau_libelle}</td>
                          <td className="px-6 py-4 text-sm text-[var(--text-muted)] max-w-[360px] truncate" title={item.description}>{item.description || '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 text-xs text-[var(--text-muted)]">{item.sous_domaine_nom} / {item.niveau_libelle} · n° {item.ordre}</td>
                          <td className="px-6 py-4 text-sm font-medium text-[var(--text-bold)] max-w-[360px] truncate" title={item.description}>{item.description || '—'}</td>
                          <td className="px-6 py-4 text-xs text-[var(--text-muted)]">{item.livrables ? `${item.livrables} livrable${item.livrables > 1 ? 's' : ''}` : '—'}</td>
                        </>
                      )}
                      <td className="px-6 py-4 text-right">
                        {!isReadOnly && (
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            <button type="button" onClick={() => handleOpenModal(item)} className="act act-icone act-contour"><Edit3 className="w-4 h-4" /></button>
                            <button type="button" onClick={() => handleDelete(item.id)} className="act act-icone act-contour"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {mainTab === 'config' && (
        <>
          <div className="flex flex-wrap gap-4 p-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-pastille w-fit">
            {CONFIG_TABS.map(t => (
              <button key={t.id} type="button" onClick={() => setConfigTab(t.id)} style={{ minHeight: 44, ...(configTab === t.id ? { backgroundColor: 'var(--accent)', color: '#0A1128' } : {}) }}
                className={`flex items-center gap-2.5 px-5 rounded-pastille text-[13px] font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] ${configTab === t.id ? 'shadow-sm' : 'text-[var(--text-main)] hover:bg-[var(--bg-main)]'}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
          {!isReadOnly && (
            <div className="flex justify-end">
              <button type="button" onClick={() => handleOpenModal()} className="act">
                <Plus className="w-4 h-4" /> Ajouter {configTab === 'niveaux' ? 'un niveau' : 'un livrable'}
              </button>
            </div>
          )}
          {configTab === 'niveaux' ? (
            <div className="bg-[var(--bg-card)] rounded-cadre overflow-hidden shadow-2xl shadow-carte">
              <table className="w-full text-left">
                <thead className="">
                  <tr>
                    <th className="w-16">N°</th>
                    <th className="">Libellé</th>
                    <th className="">Score min</th>
                    <th className="">Score max</th>
                    <th className="text-right w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {data.niveaux.length === 0 && <tr><td colSpan={5}><EtatSansDonnees cas="vide" compact titre="Aucun niveau" aide="Les niveaux de maturité traduisent le score en appréciation." action={ajouter} /></td></tr>}
                  {data.niveaux.map((n, index) => (
                    <tr key={n.id} className="hover:bg-[var(--bg-main)] group">
                      <td className="px-6 py-4 text-sm font-bold text-[var(--text-muted)]">{index + 1}</td>
                      <td className="px-6 py-4 font-bold text-[var(--text-bold)]">{n.libelle}</td>
                      <td className="px-6 py-4 text-sm text-[var(--text-muted)]">{n.min_score}</td>
                      <td className="px-6 py-4 text-sm text-[var(--text-muted)]">{n.max_score}</td>
                      <td className="px-6 py-4 text-right">
                        {!isReadOnly && (
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            <button type="button" onClick={() => handleOpenModal(n)} className="act act-icone act-contour"><Edit3 className="w-4 h-4" /></button>
                            <button type="button" onClick={() => handleDelete(n.id)} className="act act-icone act-contour"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-[var(--bg-card)] shadow-carte rounded-cadre">
                <div className="space-y-2">
                  <label className="text-[9px] font-semibold uppercase text-[var(--text-muted)] tracking-widest pl-2">Niveau</label>
                  <div className="relative">
                    <Target className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                    <select className="filtre filtre-icone" value={filterLivrableNiveau} onChange={e => setFilterLivrableNiveau(e.target.value)}>
                      <option value="all">Tous les niveaux</option>
                      {data.niveaux.map(n => <option key={n.id} value={n.id}>{n.libelle}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-end justify-start md:justify-end surtitre text-[var(--text-muted)]">
                  {filteredLivrables.length} élément(s)
                </div>
              </div>
              <div className="carte-tableau">
                <div className="tableau-hote">
                  <table className="w-full text-left min-w-[640px]">
                    <thead className="">
                      <tr>
                        <th className="w-16">N°</th>
                        <th className="">Livrable</th>
                        <th className="">Action attestée</th>
                        <th className="">Sous-domaine</th>
                        <th className="">Niveau</th>
                        <th className="text-right w-28">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border)]">
                      {filteredLivrables.length === 0 && <tr><td colSpan={6}>{filterLivrableNiveau !== 'all'
                        ? <EtatSansDonnees cas="sansFiltre" compact titre="Aucun livrable pour ce niveau"
                            action={<button type="button" className="act act-sm act-contour" onClick={() => setFilterLivrableNiveau('all')}>Réinitialiser</button>} />
                        : <EtatSansDonnees cas="vide" compact titre="Aucun livrable" action={ajouter} />}</td></tr>}
                      {filteredLivrables.map((l, index) => (
                        <tr key={l.id} className="hover:bg-[var(--bg-main)] group">
                          <td className="px-6 py-4 text-sm font-bold text-[var(--text-muted)]">{index + 1}</td>
                          <td className="px-6 py-4 text-sm text-[var(--text-bold)] max-w-[240px] truncate" title={l.description}>{l.description || '—'}</td>
                          <td className="px-6 py-4 text-xs text-[var(--text-muted)] max-w-[240px] truncate" title={l.action_description}>{l.action_description || '—'}</td>
                          <td className="px-6 py-4 text-xs text-[var(--text-muted)]">{l.sous_domaine_nom}</td>
                          <td className="px-6 py-4 text-xs text-[var(--text-muted)]">{l.niveau_libelle}</td>
                          <td className="px-6 py-4 text-right">
                            {!isReadOnly && (
                              <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                <button type="button" onClick={() => handleOpenModal(l)} className="act act-icone act-contour"><Edit3 className="w-4 h-4" /></button>
                                <button type="button" onClick={() => handleDelete(l.id)} className="act act-icone act-contour"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <AnimatePresence>
        {showModal && (
          <DiagnosticForm
            mainTab={mainTab}
            intelligenceTab={intelligenceTab}
            configTab={configTab}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSave}
            onClose={() => setShowModal(false)}
            saving={saving}
            data={data}
            flatSubdomains={flatSubdomains}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
